import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import {
    Search,
    X,
    Download,
    RefreshCw,
    MapPin,
    Eye,
    Image,
    Send,
} from "lucide-react";
import TableContainer from "Common/TableContainer";
import Modal from "Common/Components/Modal";
import { toast, ToastContainer } from "react-toastify";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import { exportToExcel } from "Common/Components/Excel";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import PaginationTest from "Common/Components/Paginationtest";
import axios from "axios";
import MapViewer from "Common/Components/MapViewer";
import { getRoleId } from "Common/Components/getRoleId";
import { useNavigate } from "react-router-dom";
import ImageLightbox from "Common/Components/ImageLightbox";
import Swal from "sweetalert2";
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

// ========== Types ==========
interface OrderImage {
    created_at: string;
    created_at_jalali: string;
    id: number;
    category: string;
    url: string;
    file_name: string;
    file_size: number;
}

interface AiTask {
    id: number;
    created_at: string;
    created_at_jalali: string;
    updated_at: string;
    updated_at_jalali: string;
    baker_name: string;
    baker_phone: string;
    driver_location_lat: number | null;
    driver_location_lng: number | null;
    auto_counted_count: number | null;
    images: OrderImage[];
}

interface TasksApiResponse {
    total: number;
    size: number;
    pages: number;
    items: AiTask[];
}

const AITasks = () => {
    const [activeTab, setActiveTab] = useState<"not_done" | "done">("not_done");
    const [tasks, setTasks] = useState<AiTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedNumber, setSelectedNumber] = useState<number>(10);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [galleryImages, setGalleryImages] = useState<OrderImage[]>([]);

    // فیلتری که دستیار سامانه فرستاده (از گفتگو یا با صدا) را برمی‌دارد
    useAssistantFilter("ai-tasks", (filter) => {
        if (filter.status) setActiveTab(filter.status);
        if (filter.pageSize) setSelectedNumber(filter.pageSize);
        setSearchTerm(filter.search || "");
        setCurrentPage(1);
    });

    useEffect(() => {
        document.body.classList.remove('overflow-hidden');
        document.body.style.overflow = '';
        const observer = new MutationObserver(() => {
            if (document.body.classList.contains('overflow-hidden')) {
                document.body.classList.remove('overflow-hidden');
                document.body.style.overflow = '';
            }
        });
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });
        return () => observer.disconnect();
    }, []);
    const navigator = useNavigate()
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editTask, setEditTask] = useState<AiTask | null>(null);
    const [autoCount, setAutoCount] = useState<string>("");
    const [submitting, setSubmitting] = useState(false);

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailTask, setDetailTask] = useState<AiTask | null>(null);

    const Token = localStorage.getItem("app_havir")
    const userRole = getRoleId(Token);

    // ========== Fetch tasks (صفحه‌بندی شده) ==========
    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = {
                page: currentPage,
                size: selectedNumber,
            };
            if (searchTerm.trim()) {
                params.search = searchTerm.trim();
            }
            const res = await axios.get<TasksApiResponse>(
                `https://rasad-api.havirard.ir/monitoring/tasks/ai-observer`,
                {
                    params: { ...params, status: activeTab },
                    headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                }
            );
            setTasks(res.data.items || []);
            setTotal(res.data.total || 0);
            setTotalPages(res.data.pages || 0);
        } catch (err: any) {
            if (err.status === 401) {
                return navigator("/login")
            }
            toast.error("خطا در دریافت لیست وظایف");
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, selectedNumber, searchTerm, activeTab]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handleSizeChange = (val: number) => {
        setSelectedNumber(val);
        setCurrentPage(1);
    };
    const handleSearchChange = (val: string) => {
        setSearchTerm(val);
        setCurrentPage(1);
    };
    const handleTabChange = (tab: "not_done" | "done") => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    // ===== تابع کمکی برای ترجمه پیام‌های خطا =====
    const translateErrorMessage = (message: string): string => {
        if (!message) return "خطای ناشناخته";
        const errorMap: { [key: string]: string } = {
            "Cannot delete driver submission while inspector submission exists":
                "امکان حذف تسک راننده وجود ندارد زیرا تسک بازرس هنوز موجود است. ابتدا تسک بازرس را حذف کنید.",
            "Cannot delete inspector submission while observer submission exists":
                "امکان حذف تسک بازرس وجود ندارد زیرا تسک ناظر هنوز موجود است. ابتدا تسک ناظر را حذف کنید.",
            "Cannot delete ai observer submission while inspector submission exists":
                "امکان حذف تسک هوش مصنوعی وجود ندارد زیرا تسک بازرس هنوز موجود است. ابتدا تسک بازرس را حذف کنید.",
            "You don't have permission": "شما دسترسی لازم برای این عملیات را ندارید.",
            "Task not found": "تسک مورد نظر یافت نشد.",
            "Invalid request": "درخواست نامعتبر است.",
            "Internal server error": "خطای داخلی سرور. لطفاً بعداً تلاش کنید.",
            "Unauthorized": "احراز هویت ناموفق. لطفاً مجدداً وارد شوید.",
            "Forbidden": "شما دسترسی به این عملیات را ندارید.",
            "Driver submission not found": "تسک راننده یافت نشد.",
            "Inspector submission not found": "تسک بازرس یافت نشد.",
            "Ai observer submission not found": "تسک هوش مصنوعی یافت نشد.",
            "Task is already completed": "این تسک قبلاً تکمیل شده است و قابل حذف نیست.",
            "Cannot delete": "امکان حذف وجود ندارد.",
            "submission not found": "ثبت‌نام مورد نظر یافت نشد.",
        };
        for (const [en, fa] of Object.entries(errorMap)) {
            if (message.includes(en)) {
                return fa;
            }
        }
        return message;
    };

    const openDELETEModal = async (Data: AiTask) => {
        const result = await Swal.fire({
            title: 'آیا مطمئن هستید؟',
            text: 'این عملیات قابل بازگشت نیست!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'بله، تأیید می‌کنم',
            cancelButtonText: 'لغو'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("app_havir");
                const response = await axios.delete(
                    `https://rasad-api.havirard.ir/monitoring/tasks/ai-observer/${Data.id}/submission`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                if (response.status === 200) {
                    await Swal.fire({
                        icon: 'success',
                        title: 'عملیات با موفقیت انجام شد',
                        text: 'تسک هوش مصنوعی مورد نظر حذف گردید.'
                    });
                    fetchTasks();
                }

            } catch (error: any) {
                let errorMessage = 'خطایی رخ داده است. لطفاً مجدداً تلاش کنید.';
                if (axios.isAxiosError(error)) {
                    const status = error.response?.status;
                    const serverMessage =
                        error.response?.data?.detail ||
                        error.response?.data?.message ||
                        error.response?.data?.error ||
                        "";
                    if (serverMessage) {
                        errorMessage = translateErrorMessage(serverMessage);
                    } else {
                        if (status === 404) {
                            errorMessage = 'تسک مورد نظر یافت نشد.';
                        } else if (status === 403) {
                            errorMessage = 'شما دسترسی لازم برای این عملیات را ندارید.';
                        } else if (status === 400) {
                            errorMessage = 'درخواست نامعتبر است.';
                        } else if (status === 409) {
                            errorMessage = 'تضاد در داده‌ها. لطفاً ابتدا وابستگی‌ها را حذف کنید.';
                        } else if (status === 500) {
                            errorMessage = 'خطای داخلی سرور. لطفاً بعداً تلاش کنید.';
                        } else if (status === 401) {
                            errorMessage = 'احراز هویت ناموفق. لطفاً مجدداً وارد شوید.';
                        }
                    }
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }
                await Swal.fire({
                    icon: 'error',
                    title: 'خطا',
                    text: errorMessage,
                    confirmButtonColor: '#d33'
                });
            }
        }
    };

    // ========== Edit Modal ==========
    const openEditModal = (task: AiTask) => {
        setEditTask(task);
        const count = task.auto_counted_count;
        setAutoCount(count != null && count > 0 ? String(count) : "");
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditTask(null);
    };
    const openGallery = (images: OrderImage[]) => {
        setGalleryImages(images || []);
        setShowGalleryModal(true);
    };

    const handleEditSubmit = async () => {
        if (!editTask) return;
        setSubmitting(true);
        try {
            await axios.put(
                `https://rasad-api.havirard.ir/monitoring/tasks/ai-observer/${editTask.id}/submission`,
                { auto_counted_count: autoCount },
                { headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
            );
            toast.success("تعداد شمارش‌شده با موفقیت ثبت شد");
            closeEditModal();
            fetchTasks();
        } catch (err: any) {
            toast.error(err.response?.data?.message || "خطا در ثبت اطلاعات");
        } finally {
            setSubmitting(false);
        }
    };

    // ========== Detail Modal ==========
    const openDetailModal = (task: AiTask) => {
        setDetailTask(task);
        setShowDetailModal(true);
    };

    // ========== دریافت همه‌ی داده‌ها برای اکسل ==========
    const fetchAllTasks = async (status: "not_done" | "done", search?: string) => {
        let allItems: AiTask[] = [];
        let page = 1;
        const size = 100;
        let hasMore = true;

        while (hasMore) {
            try {
                const params: any = { page, size, status };
                if (search?.trim()) params.search = search.trim();

                const res = await axios.get<TasksApiResponse>(
                    `https://rasad-api.havirard.ir/monitoring/tasks/ai-observer`,
                    {
                        params,
                        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                    }
                );
                const items = res.data.items || [];
                allItems = [...allItems, ...items];
                if (items.length < size) {
                    hasMore = false;
                } else {
                    page++;
                }
            } catch (error) {
                console.error("خطا در دریافت صفحه", page, error);
                hasMore = false;
                throw error;
            }
        }
        return allItems;
    };

    // ===== خروجی اکسل (همه داده‌ها) با SweetAlert2 =====
    const handleExportExcel = async () => {
        const token = localStorage.getItem("app_havir");
        if (!token) {
            toast.error("لطفاً مجدداً وارد شوید");
            navigator("/login");
            return;
        }

        Swal.fire({
            title: "در حال دریافت اطلاعات",
            text: "لطفاً منتظر بمانید...",
            icon: "info",
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        try {
            const allTasks = await fetchAllTasks(activeTab, searchTerm || undefined);

            if (allTasks.length === 0) {
                Swal.close();
                toast.warning("داده‌ای برای خروجی وجود ندارد");
                return;
            }

            const exportData = allTasks.map((t) => ({
                "شناسه": t.id,
                "نام نانوا": t.baker_name || "-",
                "شماره تماس": t.baker_phone || "-",
                "تعداد شمارش خودکار": t.auto_counted_count !== null ? t.auto_counted_count : "ثبت نشده",
                "تاریخ ایجاد": t.created_at_jalali || "-",
            }));

            exportToExcel(exportData, `AITasks-${activeTab}-full`);
            Swal.close();
            toast.success(`${exportData.length} رکورد با موفقیت صادر شد`);
        } catch (err: any) {
            Swal.close();
            console.error("Export error:", err);
            const errorMsg = err.response?.data?.message || "خطا در دریافت یا خروجی اطلاعات";
            toast.error(translateErrorMessage(errorMsg));
        }
    };

    const columns = useMemo(() => {
        const baseColumns: any[] = [
            { header: "شناسه", accessorKey: "id", enableColumnFilter: false },
            {
                header: "نام نانوا",
                accessorKey: "baker_name",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "شماره تماس",
                accessorKey: "baker_phone",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "تعداد شمارش خودکار",
                accessorKey: "auto_counted_count",
                enableColumnFilter: false,
                cell: (cell: any) =>
                    cell.getValue() !== null ? cell.getValue() : "ثبت نشده",
            },
            {
                header: "موقعیت راننده",
                accessorKey: "driver_location_lat",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const task = cell.row.original as AiTask;
                    if (task.driver_location_lat && task.driver_location_lng) {
                        return (
                            <a
                                href={`https://www.openstreetmap.org/?mlat=${task.driver_location_lat}&mlon=${task.driver_location_lng}#map=18/${task.driver_location_lat}/${task.driver_location_lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-green-600 hover:underline"
                            >
                                <MapPin size={14} />
                                نمایش
                            </a>
                        );
                    }
                    return <span className="text-gray-400">ثبت نشده</span>;
                }
            },
            {
                header: "تصاویر",
                accessorKey: "images",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const images = cell.getValue() as OrderImage[] | undefined;
                    if (images && images.length > 0) {
                        return (
                            <img
                                src={images[0].url}
                                alt="preview"
                                className="w-10 h-10 object-cover rounded cursor-pointer"
                                onClick={() => openGallery(images)}
                            />
                        );
                    }
                    return <span className="text-gray-400">بدون تصویر</span>;
                },
            },
            {
                header: "تاریخ",
                accessorKey: "created_at_jalali",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            ...(Number(userRole) === 0 || Number(userRole) === 1 ? [{
                header: "حذف",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const task = cell.row.original as AiTask;
                    return (
                        <div className="flex gap-2">
                            <button
                                onClick={() => openDELETEModal(task)}
                                className="p-2 text-red-700 border border-green-300 rounded-lg bg-green-50 hover:bg-green-100 hover:shadow-md transition-all duration-200"
                                title="حذف "
                            >
                                حذف
                            </button>
                        </div>
                    );
                }
            }] : []),
        ];

        baseColumns.push({
            header: "عملیات",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const task = cell.row.original as AiTask;
                return (
                    <div className="flex gap-2">
                        <button
                            onClick={() => openDetailModal(task)}
                            className="p-2 text-blue-500 border  border-gray-300 rounded-lg bg-blue-50 dark:hover:bg-blue-900/20"
                            title="مشاهده جزئیات"
                        >
                            مشاهده
                        </button>

                        {(userRole === 0 || userRole === 1 || userRole === 3) && (
                            <button
                                onClick={() => openEditModal(task)}
                                className="p-2 text-green-600 rounded-lg  border  border-gray-300 bg-green-50 dark:hover:bg-green-900/20"
                                title="ثبت/ویرایش شمارش"
                            >
                                ثبت / ویرایش
                            </button>
                        )}
                    </div>
                );
            },
        });

        return baseColumns;
    }, [userRole, openDetailModal, openEditModal, openGallery]);

    return (
        <React.Fragment>
            <BreadCrumb pageTitle="وظایف ناظر هوش مصنوعی" title="هاویر پایش" />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

            <div className="card">
                <div className="card-body">
                    {/* Tabs */}
                    <div className="flex gap-4 mb-4 border-b dark:border-gray-700">
                        <button
                            onClick={() => handleTabChange("not_done")}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "not_done"
                                ? "border-blue-600 text-blue-600"
                                : "border-transparent text-gray-500"
                                }`}
                        >
                            ثبت‌نشده‌ها
                        </button>
                        <button
                            onClick={() => handleTabChange("done")}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "done"
                                ? "border-blue-600 text-blue-600"
                                : "border-transparent text-gray-500"
                                }`}
                        >
                            ثبت‌شده‌ها
                        </button>
                    </div>

                    {/* Toolbar */}
                    <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-2">
                            <RefreshCw
                                className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
                                onClick={fetchTasks}
                            />
                            <h6 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                                مجموع وظایف{" "}
                                <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">
                                    {total}
                                </span>
                            </h6>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <TableDropDownSmall onSelect={handleSizeChange} />
                            <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
                                <Download size={16} /> اکسل
                            </button>
                        </div>
                    </div>
                    <div className="relative mb-2">
                        <input
                            type="text"
                            placeholder="جستجو..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                        />
                        {searchTerm && (
                            <button onClick={() => handleSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                                <X className="size-4" />
                            </button>
                        )}
                    </div>
                    {/* Table */}
                    {loading ? (
                        <LoadingCounter count={0} text="در حال بارگذاری..." />
                    ) : tasks.length === 0 ? (
                        <LoadingCounter count={0} text="وظیفه‌ای یافت نشد" />
                    ) : (
                        <>
                            <TableContainer
                                isPagination={false}
                                columns={columns}
                                data={tasks}
                                customPageSize={selectedNumber}
                                divclassName="-mx-5 overflow-x-auto"
                                tableclassName="w-full whitespace-nowrap"
                                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                                PaginationClassName="hidden"
                            />
                            <PaginationTest
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Edit Modal */}
            <Modal
                show={showEditModal}
                onHide={closeEditModal}
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[30rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
                    <Modal.Title className="text-lg font-bold">
                        ویرایش شمارش سفارش #{editTask?.id}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5">
                    <div>
                        <label className="block mb-2 text-sm font-medium">تعداد شمارش‌شده خودکار</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={autoCount}
                            onChange={(e) => setAutoCount(e.target.value)}
                            placeholder="تعداد را وارد کنید"
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer className="flex justify-end gap-3 p-5 border-t dark:border-gray-700">
                    <button onClick={closeEditModal} className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600">
                        انصراف
                    </button>
                    <button onClick={handleEditSubmit} disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        {submitting ? "در حال ثبت..." : "ثبت"}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Detail Modal */}
            <Modal
                show={showDetailModal}
                onHide={() => setShowDetailModal(false)}
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[36rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
                    <Modal.Title className="text-lg font-bold">جزئیات سفارش #{detailTask?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5">
                    {detailTask && (
                        <div className="space-y-3 text-sm">
                            <div><strong>نانوا:</strong> {detailTask.baker_name}</div>
                            <div><strong>تلفن نانوا:</strong> {detailTask.baker_phone}</div>
                            <div><strong>تعداد شمارش خودکار:</strong> {detailTask.auto_counted_count ?? "ثبت نشده"}</div>
                            <div>
                                <strong className="flex items-center gap-1 mb-2">
                                    <MapPin size={16} /> موقعیت راننده:
                                </strong>
                                {detailTask.driver_location_lat != null && detailTask.driver_location_lng != null ? (
                                    <div className="-mx-5">
                                        <MapViewer
                                            lat={detailTask.driver_location_lat}
                                            lng={detailTask.driver_location_lng}
                                        />
                                    </div>
                                ) : (
                                    <p className="text-gray-400">ثبت نشده</p>
                                )}
                            </div>
                            <div>
                                <strong>تصاویر:</strong>{" "}
                                {detailTask.images?.length > 0 ? (
                                    <button
                                        onClick={() => openGallery(detailTask.images)}
                                        className="text-blue-600 underline hover:text-blue-800"
                                    >
                                        {detailTask.images.length} عکس (مشاهده گالری)
                                    </button>
                                ) : (
                                    "ندارد"
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="flex justify-end p-5 border-t dark:border-gray-700">
                    <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600">
                        بستن
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Gallery Modal */}
            <Modal
                show={showGalleryModal}
                onHide={() => setShowGalleryModal(false)}
                modal-center="true"
                className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
                dialogClassName="w-[90vw] md:w-[56rem] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
                    <Modal.Title className="text-lg text-white font-bold">جهت بزرگ نمایی روی عکس کلیک کنید</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
                    {galleryImages.length === 0 ? (
                        <p className="text-center text-white/50">تصویری وجود ندارد</p>
                    ) : (
                        (() => {
                            const preImages = galleryImages.filter(img => img.category === "pre_unloading");
                            const postImages = galleryImages.filter(img => img.category === "post_unloading");
                            const otherImages = galleryImages.filter(img => img.category !== "pre_unloading" && img.category !== "post_unloading");

                            return (
                                <div className="space-y-6">
                                    {preImages.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-white mb-2 border-b border-white/10 pb-1">
                                                📸 قبل از تخلیه ({preImages.length} عکس)
                                            </h4>
                                            <div className="grid grid-cols-2 gap-4">
                                                {preImages.map((img, idx) => (
                                                    <div
                                                        key={img.id}
                                                        className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
                                                        onClick={() => {
                                                            const globalIdx = galleryImages.findIndex(i => i.id === img.id);
                                                            setLightboxIndex(globalIdx);
                                                            setLightboxOpen(true);
                                                        }}
                                                    >
                                                        <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                                                        <div className="p-2 text-xs bg-white/5">قبل از تخلیه | {img.file_name}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {postImages.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-white mb-2 border-b border-white/10 pb-1">
                                                📸 بعد از تخلیه ({postImages.length} عکس)
                                            </h4>
                                            <div className="grid grid-cols-2 gap-4">
                                                {postImages.map((img, idx) => (
                                                    <div
                                                        key={img.id}
                                                        className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
                                                        onClick={() => {
                                                            const globalIdx = galleryImages.findIndex(i => i.id === img.id);
                                                            setLightboxIndex(globalIdx);
                                                            setLightboxOpen(true);
                                                        }}
                                                    >
                                                        <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                                                        <div className="p-2 text-xs bg-white/5">بعد از تخلیه | {img.file_name}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {otherImages.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-white mb-2 border-b border-white/10 pb-1">
                                                📸 سایر تصاویر ({otherImages.length} عکس)
                                            </h4>
                                            <div className="grid grid-cols-2 gap-4">
                                                {otherImages.map((img, idx) => (
                                                    <div
                                                        key={img.id}
                                                        className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
                                                        onClick={() => {
                                                            const globalIdx = galleryImages.findIndex(i => i.id === img.id);
                                                            setLightboxIndex(globalIdx);
                                                            setLightboxOpen(true);
                                                        }}
                                                    >
                                                        <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                                                        <div className="p-2 text-xs bg-white/5">{img.category} | {img.file_name}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()
                    )}
                </Modal.Body>
                <Modal.Footer className="flex justify-end p-5 border-t border-white/10">
                    <button onClick={() => setShowGalleryModal(false)} className="px-4 py-2 text-sm bg-white/10 rounded-lg hover:bg-white/20">
                        بستن
                    </button>
                </Modal.Footer>
            </Modal>
            {lightboxOpen && (
                <ImageLightbox
                    images={galleryImages}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </React.Fragment>
    );
};

export default AITasks;